import { useRef, useState } from 'react'
import { post } from '../lib/api'
import Icon from './Icon'

/**
 * Drop a credit report in.
 *
 * ── The PDF never leaves the browser ─────────────────────────────────────────
 *
 * A credit report carries your full account list and address history. The copy
 * that never leaves the device cannot leak from a server, and the machine
 * already holding the file can decode it for free. So the file is turned into
 * TEXT here, in the tab, and only the text is posted.
 *
 * This first said the Workers CPU budget made server-side decoding impossible,
 * copied from a comment in 42payments that said 10ms/free plan. The account is
 * on Workers Paid and that comment was false; it was corrected on 2026-09-03.
 * The design did not change — privacy was always the better argument — but the
 * reason had to.
 *
 * pdf.js is loaded with a dynamic import, so none of it is in the main bundle.
 * A person who never uploads a report never downloads the library.
 *
 * ── And this app stores none of it ───────────────────────────────────────────
 *
 * Standard section 20: 42payments owns credit history. The text is forwarded
 * there and the numbers come back only when the next pull fetches them, which
 * is why the last step of a successful upload is the pull.
 */

type Result = {
  ok: boolean
  applied: boolean
  bureau?: string
  pulledOn?: string | null
  tradelines?: number
  expected?: number
  complete?: boolean
  problems?: string[]
  preview?: Array<{ creditor: string | null; last4: string | null; balanceCents: number | null; limitCents: number | null }>
  error?: string
}

const BUREAUS = ['equifax', 'experian', 'transunion'] as const

/** Pull the text out of a PDF, page by page, in this tab. */
async function pdfToText(file: File, onPage: (n: number, of: number) => void): Promise<string> {
  const pdfjs = await import('pdfjs-dist')
  /*
   * The worker is bundled and served from this origin. The CSP here is
   * `script-src 'self'`, so a worker fetched from a CDN would be blocked with
   * no visible error — the extraction would simply never finish.
   */
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise
  const pages: string[] = []
  for (let i = 1; i <= doc.numPages; i++) {
    onPage(i, doc.numPages)
    const content = await doc.getPage(i).then((p) => p.getTextContent())
    pages.push(content.items.map((it) => ('str' in it ? it.str : '')).join(' '))
  }
  return pages.join('\n')
}

const money = (cents: number | null | undefined) =>
  typeof cents === 'number' ? (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—'

export default function CreditUpload({ onImported }: { onImported: () => void }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [bureau, setBureau] = useState<string>('')
  const [pulledOn, setPulledOn] = useState<string>(new Date().toISOString().slice(0, 10))
  const [text, setText] = useState<string | null>(null)
  const [filename, setFilename] = useState<string>('')
  const fileInput = useRef<HTMLInputElement>(null)

  async function read(file: File) {
    setError(null); setResult(null); setText(null)
    setFilename(file.name)
    try {
      setBusy('Reading the file…')
      const extracted = await pdfToText(file, (n, of) => setBusy(`Reading page ${n} of ${of}…`))
      if (!extracted.trim()) {
        /*
         * A scanned report is images, not text, and pdf.js will return nothing
         * rather than fail. Saying "no text" is the difference between a fixable
         * problem and a mystery.
         */
        setError('That PDF has no text in it — it is probably a scan. Download the report again from annualcreditreport.com rather than printing it.')
        return
      }
      setText(extracted)
      setBusy('Checking what it parses to…')
      // Dry run first, always. The bureau tells you if its layout moved.
      setResult(await post<Result>('/api/credit-report', { text: extracted, bureau: bureau || undefined, pulledOn, filename: file.name }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    } finally {
      setBusy(null)
    }
  }

  async function apply() {
    if (!text) return
    setError(null)
    try {
      setBusy('Storing it in 42payments…')
      const out = await post<Result>('/api/credit-report', { text, bureau: bureau || undefined, pulledOn, filename, apply: true })
      setResult(out)
      if (out.applied) {
        // The numbers live in the hub, so the screen only changes after a pull.
        setBusy('Pulling the new figures…')
        await post('/api/pull/credit')
        onImported()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not store that report.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="rg-card">
      <p className="eyebrow" style={{ color: 'var(--brand)' }}>Credit reports</p>
      <h2>Add a report</h2>
      <p className="rg-lede">
        The PDF is read in this tab and never uploaded &mdash; only the text it
        contains is sent, and it is stored in 42payments, which owns your credit
        history.
      </p>

      <div className="cu-row">
        <label>
          Bureau
          <select value={bureau} onChange={(e) => setBureau(e.target.value)}>
            <option value="">detect from the file</option>
            {BUREAUS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label>
          Pulled on
          <input type="date" value={pulledOn} onChange={(e) => setPulledOn(e.target.value)} />
        </label>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,.pdf"
        className="cu-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f) }}
      />
      <button type="button" className="cu-drop" onClick={() => fileInput.current?.click()} disabled={!!busy}>
        <Icon name="plus" size={18} />
        {busy ?? (filename ? `${filename} — choose another` : 'Choose a report PDF')}
      </button>

      {error && <p className="error" role="alert">{error}</p>}

      {result && (
        <div className="cu-out">
          {result.problems?.length ? (
            <div className="rg-note">
              <b>This did not parse cleanly &mdash; nothing was stored</b>
              <p>{result.problems.join('. ')}. The layout has probably changed; the CLI importer in <code>~/42payments</code> is the fallback.</p>
            </div>
          ) : result.applied ? (
            <div className="rg-note" style={{ background: 'color-mix(in srgb, var(--ok) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--ok) 25%, transparent)' }}>
              <b style={{ color: 'var(--ok)' }}>Stored &mdash; {result.tradelines} tradelines from {result.bureau}</b>
              <p>
                {result.complete === false && <>Only {result.tradelines} of {result.expected} landed. </>}
                The figures on this page have been refreshed.
              </p>
            </div>
          ) : (
            <>
              <p className="rg-foot">
                Parsed <strong>{result.tradelines}</strong> tradelines from <strong>{result.bureau}</strong>. Nothing stored yet.
              </p>
              <table className="rg-ledger">
                <tbody>
                  {(result.preview ?? []).map((t, i) => (
                    <tr key={i}>
                      <td>{t.creditor ?? '—'}{t.last4 && <span className="muted"> …{t.last4}</span>}</td>
                      <td>{money(t.balanceCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn-s btn-go" onClick={() => void apply()} disabled={!!busy}>
                Store it and pull the new figures
              </button>
            </>
          )}
        </div>
      )}
    </section>
  )
}
